import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NeoBreadcrumbModule, NeoBreadcrumbService } from '@neogov/pattern-library/neo-breadcrumb';
import { CmBreadcrumbService } from './cm-breadcrumb.service';

/**
 * Thin wrapper over the pattern-library `neo-breadcrumb`.
 *
 * The trail is derived automatically from the router: a crumb is rendered for
 * each ancestor route that defines `data: { breadcrumb: <string | (data) => string> }`.
 * Routes without a `breadcrumb` in their chain contribute no crumb.
 *
 * `neo-breadcrumb` always renders its `<nav>` even with an empty trail, which
 * would leave an empty breadcrumb landmark (and vertical gap) on the many pages
 * that should NOT show one. So this wrapper only renders it when the current
 * route actually has crumbs — top-level/nav pages render nothing.
 *
 * Provides the second wayfinding mechanism required by WCAG 2.4.5 (Multiple
 * Ways): the persistent navigation menu is the first, this breadcrumb the second.
 */
@Component({
  selector: 'cm-breadcrumb',
  imports: [NeoBreadcrumbModule],
  // Alias the pattern-library token to our root CmBreadcrumbService so the inner
  // neo-breadcrumb renders our (route-config-based, dedup-safe) trail. useExisting
  // resolves the root singleton — not a new instance — and travels with the
  // component, so no app-config wiring or per-spec setup is needed.
  providers: [{ provide: NeoBreadcrumbService, useExisting: CmBreadcrumbService }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.less',
})
export class BreadcrumbComponent {
  /** Accessible name for the breadcrumb navigation landmark. */
  public readonly ariaLabel = input<string>('Breadcrumb');
  /** Crumbs kept visible on each side of the ellipsis when the trail collapses. */
  public readonly maxVisible = input<number>(1);
  public readonly autId = input<string>('cm-breadcrumb');

  private readonly _breadcrumbService = inject(NeoBreadcrumbService);
  private readonly _breadcrumbs = toSignal(this._breadcrumbService.breadcrumbs$, {
    initialValue: [],
  });

  /** True only when the current route contributes at least one crumb. */
  protected readonly hasBreadcrumbs = computed(() => this._breadcrumbs().length > 0);
}
